import React, { useCallback, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Typography } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

// A instalacao so pode ser solicitada por gesto explicito do usuario.
// Estado compartilhado entre login e menu, sem persistir mensagens ou credenciais.
let deferredPrompt = null;
let installCompleted = false;
const subscribers = new Set();
const announce = () => subscribers.forEach((callback) => callback());

const inStandalone = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.navigator.standalone) ||
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    announce();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installCompleted = true;
    announce();
  });
}

const InstallAppButton = ({ compact = false }) => {
  const [visible, setVisible] = useState(() => !installCompleted && !inStandalone());
  const [showInstructions, setShowInstructions] = useState(false);
  const [prompting, setPrompting] = useState(false);

  useEffect(() => {
    const refresh = () => setVisible(!installCompleted && !inStandalone());
    subscribers.add(refresh);
    const media = typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;
    if (media) {
      if (media.addEventListener) media.addEventListener("change", refresh);
      else if (media.addListener) media.addListener(refresh);
    }
    return () => {
      subscribers.delete(refresh);
      if (media) {
        if (media.removeEventListener) media.removeEventListener("change", refresh);
        else if (media.removeListener) media.removeListener(refresh);
      }
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (installCompleted || inStandalone() || prompting) return;
    if (!deferredPrompt) {
      setShowInstructions(true);
      return;
    }
    const prompt = deferredPrompt;
    deferredPrompt = null; // A API permite um unico uso por evento.
    setPrompting(true);
    announce();
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        // A ocultacao definitiva depende do evento appinstalled ou standalone.
        setShowInstructions(false);
      }
    } catch (_) {
      setShowInstructions(true);
    } finally {
      setPrompting(false);
    }
  }, [prompting]);

  if (!visible) return null;

  return (
    <>
      {compact ? (
        <MenuItem onClick={handleInstall} disabled={prompting}>
          <GetAppIcon fontSize="small" style={{ marginRight: 10 }} />
          Instalar aplicativo
        </MenuItem>
      ) : (
        <div style={{ width: "100%", margin: "12px 0 14px", padding: "14px", background: "rgba(57,172,231,0.10)", border: "1px solid rgba(57,172,231,0.38)", borderRadius: 12 }}>
          <Typography style={{ textAlign: "center", color: "#dbeafe", fontWeight: 600, fontSize: 13, marginBottom: 9 }}>
            Acesse seus atendimentos com um toque
          </Typography>
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleInstall}
            disabled={prompting}
            startIcon={<GetAppIcon />}
            aria-label="Instalar Ventura Atende no computador ou celular"
            style={{ background: "linear-gradient(90deg, #39ACE7, #3b82f6)", color: "#fff", fontWeight: 700, textTransform: "none", borderRadius: 8, minHeight: 44, boxShadow: "0 4px 16px rgba(57,172,231,0.22)" }}
          >
            {prompting ? "Abrindo instalador..." : "Instalar Ventura Atende"}
          </Button>
          <Typography style={{ textAlign: "center", color: "#b6c7db", marginTop: 8, fontSize: 11 }}>
            Disponivel para computador e celular
          </Typography>
        </div>
      )}
      <Dialog open={showInstructions} onClose={() => setShowInstructions(false)} maxWidth="xs" fullWidth aria-labelledby="pwa-install-title">
        <DialogTitle id="pwa-install-title">Instalar Ventura Atende</DialogTitle>
        <DialogContent dividers>
          {isIOS() ? (
            <>
              <Typography paragraph>Para instalar no iPhone ou iPad:</Typography>
              <Typography paragraph>1. Abra esta pagina no Safari.</Typography>
              <Typography paragraph>2. Toque no botao <strong>Compartilhar</strong>.</Typography>
              <Typography>3. Selecione <strong>Adicionar a Tela de Inicio</strong> e confirme.</Typography>
            </>
          ) : (
            <>
              <Typography paragraph>Seu navegador nao ofereceu a instalacao automatica neste momento.</Typography>
              <Typography paragraph>No <strong>Chrome ou Edge</strong>, abra o menu do navegador e procure <strong>Instalar aplicativo</strong> ou <strong>Instalar esta pagina como aplicativo</strong>.</Typography>
              <Typography>Use uma conexao HTTPS. Quando o navegador permitir a instalacao, este botao abrira o instalador diretamente.</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setShowInstructions(false)}>Entendi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
